import { useState, useEffect } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Modal,
  Pagination,
  Spinner,
} from "react-bootstrap";
import { useFindUsers } from "../../api/queries/useFindUser";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import { BsTrash } from "react-icons/bs";
import { useDeleteUser } from "../../api/mutations/useDeleteUser";
import { User } from "../../types/Types";
import EditModal from "../Account/EditModal";
import { BsThreeDotsVertical } from "react-icons/bs";
import { useMediaQuery } from "react-responsive";

const ManageUsers = () => {
  const [search, setSearch] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [userData, setUserData] = useState<User | null>(null);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  const isTablet = useMediaQuery({ minWidth: 768 });

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const {
    data: userResponse,
    refetch: refetchUsers,
    isLoading,
  } = useFindUsers({
    pageSize,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
  });

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteUser({
    onSuccess: () => {
      setShowDeactivateModal(false);
      refetchUsers();
    },
  });

  const transformUserData = (user: User) => {
    return {
      user: {
        ...user,
      },
      imageUrls: {
        IdDocument: user.IdDocument || "",
        ProfessionalPhoto: user.ProfessionalPhoto || "",
        BusinessLogo: user.BusinessLogo || "",
        PersonalProfessionalPhoto: user.ProfessionalPhoto || "",
      },
    };
  };

  const mergedUsers = (userResponse?.users.data || []).map((user) => {
    const imageEntry = userResponse?.userImageUrls?.find(
      (img) => img.userId === user.id
    );

    return {
      ...user,
      ...imageEntry?.imageUrls,
    };
  });

  const navigate = useNavigate();

  const handleEdit = (user: User) => {
    if (user) {
      setShowEditModal(true);
      setUserData(user);
    }
  };

  const handleDeactivate = () => {
    if (userData?.id !== undefined) {
      deleteUser(userData.id);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    refetchUsers();
  };

  const totalPages = userResponse?.users.totalPages || 1;

  // Build page numbers for pagination
  const pageNumbers = [];
  for (let i = 1; i <= totalPages; i++) {
    pageNumbers.push(i);
  }

  return (
    <div className="container mt-2">
      <Form.Control
        type="text"
        placeholder="Search users..."
        className="mb-4"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* Loader: Display spinner while data is loading */}
      {isLoading ? (
        <div className="d-flex p-4 justify-content-center">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <Row>
          {mergedUsers.map((conn, idx) => (
            <Col
              xs={12}
              key={idx}
              className="p-2 user-card border-bottom d-flex flex-row justify-content-between align-items-center"
            >
              <div
                className="d-flex gap-3 align-items-start cursor-pointer"
                onClick={() => navigate(`/user-profile/${conn.objectId}`)}
              >
                <img
                  src={
                    conn?.ProfessionalPhoto ||
                    conn?.PersonalProfessionalPhoto ||
                    "/user.png"
                  }
                  className="rounded-circle"
                  width={56}
                  height={56}
                  alt={"user"}
                />
                <div className="d-flex flex-column" style={{maxWidth: isTablet ? "100%" : "150px"}}>
                  <div className="fw-semibold text-truncate w-100">
                    {conn.firstName + " " + conn.lastName}
                  </div>
                  <div
                    data-toggle="tooltip"
                    data-placement="top"
                    title="Tooltip on top"
                    className="text-muted small text-truncate w-100 w-md-100"
                  >
                    {conn.email}
                  </div>
                </div>
              </div>
              <div className="dropdown">
                <button
                  className="btn btn-link p-0"
                  type="button"
                  id={`dropdownMenuButton-${idx}`}
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  <BsThreeDotsVertical size={20} />
                </button>
                <ul
                  className="dropdown-menu mt-1"
                  aria-labelledby={`dropdownMenuButton-${idx}`}
                >
                  <li>
                    <button
                      className="dropdown-item"
                      onClick={() => handleEdit(conn)}
                    >
                      <FaEdit size={16} className="me-2" />
                      Edit
                    </button>
                  </li>
                  <li>
                    <button
                      className="dropdown-item text-danger"
                      onClick={() => {
                        setShowDeactivateModal(true);
                        setUserData(conn);
                      }}
                    >
                      <BsTrash size={16} className="me-2" />
                      Deactivate
                    </button>
                  </li>
                </ul>
              </div>
            </Col>
          ))}
        </Row>
      )}

      {/* Pagination Controls */}
      {!isLoading && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {pageNumbers.map((pageNum) => (
              <Pagination.Item
                key={pageNum}
                active={pageNum === currentPage}
                onClick={() => handlePageChange(pageNum)}
              >
                {pageNum}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      <EditModal
        showEditModal={showEditModal}
        setShowEditModal={() => setShowEditModal(false)}
        userData={userData ? transformUserData(userData) : null}
      />
      <Modal
        centered
        show={showDeactivateModal}
        onHide={() => setShowDeactivateModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate your account? This action cannot
          be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setShowDeactivateModal(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDeactivate}
            disabled={isDeleting}
          >
            {isDeleting ? "Deactivating..." : "Deactivate"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageUsers;
